import { type QueryFunctionContext, useQuery } from '@tanstack/react-query';

import { getCustomerSearch, getQuickCustomerSearch } from 'queries';
import { Toast, parseErrors } from 'helpers';
import type { Arg0, Customer, JsonDataItem } from 'types';

const queryGroup = 'CUSTOMER_SEARCH';

type FilterChoices = Arg0<typeof getCustomerSearch>['filter'];
type SearchByChoices = Arg0<typeof getCustomerSearch>['searchParam'];

type SearchParams = {
  filter?: FilterChoices;
  searchBy: SearchByChoices;
  searchValue: string;
  recordsPerPage?: number;
  page?: number;
};

async function queryFn({
  queryKey,
}: QueryFunctionContext<[string, SearchParams]>): Promise<{ customers: Customer[]; meta: any }> {
  const [_, params] = queryKey;

  const { searchBy, searchValue, page, recordsPerPage, filter } = params;
  const isQuickSearch = searchBy === 'all';

  const response = isQuickSearch
    ? await getQuickCustomerSearch({
        searchValue,
        page,
        recordsPerPage,
      })
    : await getCustomerSearch({ filter, searchParam: searchBy, searchValue, page, recordsPerPage });

  return { customers: response.data, meta: response.meta };
}

type CustomersWithMetadata = [customers?: Customer[], totalCustomers?: number];

function useCustomerSearch({
  filter = '',
  searchBy = 'mobile',
  searchValue = '',
  page = 1,
  recordsPerPage = 5,
}: Partial<SearchParams> = {}): CustomersWithMetadata {
  const { data } = useQuery({
    queryFn,
    queryKey: [queryGroup, { filter, searchBy, searchValue, page, recordsPerPage }] as [string, SearchParams],
    onError(err: JsonDataItem) {
      Toast.show({ description: parseErrors(err).join(', '), type: Toast.Type.Error });
    },
  });
  return [data?.customers, data?.meta.total];
}

export { useCustomerSearch };
